import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect } from 'react';

import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { logger } from '@/services/logger';
import { appendEscapeParam, encodePathSegment, restUrl } from '@/services/utils';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

export function useItemValidationUpdater(dirtyItemIdsRef: any) {

    const queryClient = useQueryClient();

    const fetchItemById = async (id: string, options: { validate?: boolean } = {}) => {
        const esc = '~';
        const query = options.validate ? '?validate=true' : '';
        const url = appendEscapeParam(
            restUrl(`items/${encodePathSegment(id, esc)}${query}`),
            esc, id
        );
        const response = await axios.get(url);
        return response.data;
    };

    const {subscribe, unsubscribe} = useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.VALIDATION_FINISHED_EVENT,
        identifier: undefined,
        eventHandler: async (data: any) => {
            if (!data?.validationResults) return;

            const validatedItemIds = Object.keys(data.validationResults);
            const dirtyValidatedIds = dirtyItemIdsRef.current.filter((id: any) =>
                validatedItemIds.includes(id.toString())
            );

            if (dirtyValidatedIds.length === 0) return;

            try {
                for (const id of dirtyValidatedIds) {
                    const updatedItem = await fetchItemById(id, { validate: true });

                    const allQueries = queryClient.getQueriesData({ queryKey: ['items'] });

                    allQueries.forEach(([key, oldData]: any) => {
                        if (!oldData) return;

                        const newPages = oldData.pages.map((page: any) => ({
                            ...page,
                            data: page.data.map((item: any) =>
                                item.primaryKey__ === updatedItem.primaryKey__
                                    ? { ...item, ...updatedItem }
                                    : item
                            ),
                        }));

                        queryClient.setQueryData(key, { ...oldData, pages: newPages });
                    });
                }
            } catch (error) {
                logger.debug('Failed to fetch or patch item:', error);
            }
        },

    });

    useEffect(() => {
        if (dirtyItemIdsRef.current.length === 0) return;

        subscribe(true);

        return () => {
            unsubscribe();
        };
    }, [dirtyItemIdsRef, subscribe, unsubscribe]);

}

